import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

// Cast member interface (for Messages tab use)
interface CastMember {
  uid: string;
  name: string;
  profilePictureUrl?: string;
  role: 'student' | 'teacher';
}

// Query keys for consistent caching
export const messagesQueryKeys = {
  all: ['messages'] as const,
  byShow: (showId: string) => [...messagesQueryKeys.all, showId] as const,
  streamToken: (userId?: string) => ['streamToken', userId] as const,
};

// Generate Stream Chat authentication token
const fetchStreamToken = async () => {
  const generateToken = httpsCallable(functions, 'generateStreamToken');
  const result = await generateToken();
  const data = result.data as {
    token: string;
    apiKey: string;
    userId: string;
  };

  return data;
};

// Hook for Stream token with user-specific caching
export const useStreamToken = (userId?: string) => {
  return useQuery({
    queryKey: messagesQueryKeys.streamToken(userId),
    queryFn: fetchStreamToken,
    staleTime: 50 * 60 * 1000, // 50 minutes - tokens usually last 1 hour
    gcTime: 60 * 60 * 1000, // 1 hour
    enabled: !!userId,
    retry: 3, // Retry token generation failures
    refetchOnWindowFocus: false,
  });
};

// Hook for mutations (channel initialization)
export const useMessagesMutations = (showId: string) => {
  const queryClient = useQueryClient();

  // Initialize show channels mutation
  const initializeChannels = useMutation({
    mutationFn: async () => {
      const initChannels = httpsCallable(functions, 'initializeShowChannels');
      await initChannels({ showId });
    },
    // No cache invalidation needed - this is a side-effect, not data fetching
  });

  return {
    initializeChannels,
  };
};

// Firebase UID for a cast row. getCastData sends `userId` alongside the
// composite id (`{showId}_{uid}`); prefer it, and fall back to the part after
// the first underscore for rows that only carry the id.
const uidForCastRow = (row: any): string | null => {
  if (typeof row?.userId === 'string' && row.userId) return row.userId;
  if (typeof row?.id !== 'string') return null;
  const separator = row.id.indexOf('_');
  return separator >= 0 ? row.id.slice(separator + 1) || null : null;
};

// Helper function to process raw cast data into CastMember objects.
//
// Tolerant of the trimmed roster a plain student or leader receives from
// getCastData (name, photo and role only - see memberCastRow in
// functions/src/api/shows/queries.ts): only id, name and photo are read, and
// a missing list is an empty one.
export const processCastData = (
  castData: { teachers?: any[] | null, students?: any[] | null } | undefined | null,
  currentUserId?: string
): CastMember[] => {
  if (!castData || !currentUserId) return [];
  
  const members: CastMember[] = [];
  
  // Add students
  (castData.students ?? []).forEach((student: any) => {
    const firebaseUID = uidForCastRow(student);
    
    if (firebaseUID && firebaseUID !== currentUserId) { // Don't include current user
      members.push({
        uid: firebaseUID,
        name: student.fullName || 'Unknown Student',
        profilePictureUrl: student.profilePictureUrl || undefined,
        role: 'student'
      });
    }
  });

  // Add teachers
  (castData.teachers ?? []).forEach((teacher: any) => {
    const firebaseUID = uidForCastRow(teacher);
    
    if (firebaseUID && firebaseUID !== currentUserId) { // Don't include current user
      members.push({
        uid: firebaseUID,
        name: teacher.fullName || 'Unknown Teacher',
        profilePictureUrl: teacher.profilePictureUrl || undefined,
        role: 'teacher'
      });
    }
  });

  return members;
};

// Export types for other components
export type { CastMember };